"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";

interface SafeWebGLBoundaryProps {
	children: ReactNode;
	/** Shown when a child throws (WebGL context / shader failures). */
	fallback?: ReactNode;
}

interface SafeWebGLBoundaryState {
	failed: boolean;
}

/**
 * Decorative WebGL must not take down the surrounding product surface.
 * Used around OGL/R3F effects that can throw when a context cannot be created.
 */
export class SafeWebGLBoundary extends Component<
	SafeWebGLBoundaryProps,
	SafeWebGLBoundaryState
> {
	state: SafeWebGLBoundaryState = { failed: false };

	static getDerivedStateFromError(): SafeWebGLBoundaryState {
		return { failed: true };
	}

	componentDidCatch(error: Error, _errorInfo: ErrorInfo): void {
		if (process.env.NODE_ENV !== "production") {
			console.warn("[webgl] decorative effect failed", error.message);
		}
	}

	render(): ReactNode {
		if (this.state.failed) {
			return this.props.fallback ?? null;
		}
		return this.props.children;
	}
}
